import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Link as MuiLink,
  Container,
  Grid,
  Divider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { acceptInvitation, getInvitationInfo } from '../../api/invitations';
import { Link as RouterLink } from 'react-router-dom';

export const AcceptInvitation: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [loadingInfo, setLoadingInfo] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [invitationInfo, setInvitationInfo] = useState<any>(null);
  const [expired, setExpired] = useState(false);

  // Form data
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    company: ''
  });

  // Get token from URL
  const token = new URLSearchParams(location.search).get('token');

  useEffect(() => {
    if (!token) {
      setError('Invalid invitation link. No token provided.');
      setLoadingInfo(false);
      return;
    }

    const loadInvitationInfo = async () => {
      try {
        const info = await getInvitationInfo(token);
        setInvitationInfo(info);
        
        // Check if invitation is expired
        const now = new Date();
        const expiresAt = new Date(info.expires_at);
        if (expiresAt < now) {
          setExpired(true);
          setError('This invitation has expired.');
        } else if (info.is_used) {
          setError('This invitation has already been used.');
        } else {
          // Pre-fill form with invitation data
          setFormData(prev => ({
            ...prev,
            first_name: info.first_name || '',
            last_name: info.last_name || '',
            company: info.company || ''
          }));
        }
      } catch (err: any) {
        console.error('Error loading invitation info:', err);
        setError(err.response?.data?.detail || 'Failed to load invitation details.');
      } finally {
        setLoadingInfo(false);
      }
    };

    loadInvitationInfo();
  }, [token]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match.');
      setLoading(false);
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long.');
      setLoading(false);
      return;
    }

    try {
      if (!token) {
        throw new Error('No invitation token available');
      }

      await acceptInvitation({
        invitation_token: token,
        password: formData.password,
        first_name: formData.first_name,
        last_name: formData.last_name,
        company: formData.company
      });

      navigate('/auth', {
        replace: true,
        state: { inviteLoginEmail: invitationInfo?.email?.trim() || undefined },
      });

    } catch (err: any) {
      console.error('Error accepting invitation:', err);
      setError(err.response?.data?.detail || 'Failed to create account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = () => {
    return formData.password && 
           formData.confirmPassword && 
           formData.password === formData.confirmPassword &&
           formData.password.length >= 8;
  };

  if (loadingInfo) {
    return (
      <Dialog open fullScreen disableEscapeKeyDown onClose={() => {}}>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
          <CircularProgress />
        </DialogContent>
      </Dialog>
    );
  }

  if (!token || error) {
    return (
      <Dialog open fullScreen disableEscapeKeyDown onClose={() => {}}>
        <DialogTitle>Invalid Invitation</DialogTitle>
        <DialogContent>
          <Container maxWidth="sm" sx={{ py: 2 }}>
            <Paper elevation={6} sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h4" component="h1" gutterBottom color="error">
                Invalid Invitation
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                {error || 'This invitation link is invalid or has expired.'}
              </Typography>
              <Button
                component={RouterLink}
                to="/auth"
                variant="contained"
                size="large"
              >
                Go to Login
              </Button>
            </Paper>
          </Container>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open fullScreen disableEscapeKeyDown onClose={() => {}}>
      <DialogContent sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center' }}>
        <Container maxWidth="sm" sx={{ my: 4 }}>
      <Paper elevation={6} sx={{ p: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Accept Invitation
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Complete your account setup to join the Productive Capabilities Data Lab
          </Typography>
        </Box>

        {invitationInfo && (
          <Box sx={{ mb: 4, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="h6" gutterBottom>
              Invitation Details
            </Typography>
            {/* Email row with truncation and chip separated to avoid overlap */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}>
              <Box sx={{ minWidth: 0 /* allow truncation */ }}>
                <Typography variant="body2" color="text.secondary">
                  Email
                </Typography>
                <Typography
                  variant="body1"
                  fontWeight="medium"
                  noWrap
                  sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
                  title={invitationInfo.email}
                >
                  {invitationInfo.email}
                </Typography>
              </Box>
              <Box sx={{ flexShrink: 0, textAlign: 'right' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Role
                </Typography>
                <Chip
                  label={invitationInfo.role?.toUpperCase() || 'USER'}
                  size="small"
                  color={invitationInfo.role === 'admin' ? 'primary' : 'default'}
                />
              </Box>
            </Box>
            {/* Expires */}
            <Box>
              <Typography variant="body2" color="text.secondary">
                Expires
              </Typography>
              <Typography variant="body1">
                {new Date(invitationInfo.expires_at).toLocaleString()}
              </Typography>
            </Box>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            Account Setup
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.first_name}
                onChange={(e) => handleInputChange('first_name', e.target.value)}
                disabled={loading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={formData.last_name}
                onChange={(e) => handleInputChange('last_name', e.target.value)}
                disabled={loading}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Organization"
                value={formData.company}
                onChange={(e) => handleInputChange('company', e.target.value)}
                disabled={loading}
                InputLabelProps={{ shrink: true }}
                autoComplete="organization"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Password *"
                type="password"
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                required
                disabled={loading}
                helperText="Password must be at least 8 characters long"
                InputLabelProps={{ shrink: true }}
                autoComplete="new-password"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Confirm Password *"
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                required
                disabled={loading}
                error={formData.confirmPassword !== '' && formData.password !== formData.confirmPassword}
                helperText={
                  formData.confirmPassword !== '' && formData.password !== formData.confirmPassword
                    ? 'Passwords do not match'
                    : ''
                }
                InputLabelProps={{ shrink: true }}
                autoComplete="new-password"
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={!isFormValid() || loading || expired}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </Button>
          </Box>
        </form>

        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Already have an account?{' '}
            <MuiLink component={RouterLink} to="/auth">
              Sign in here
            </MuiLink>
          </Typography>
        </Box>
      </Paper>
        </Container>
      </DialogContent>
    </Dialog>
  );
};